import Hero from '../../Hero/Hero'
import Breadcrumbs from '../../Breadcrumbs/Breadcrumbs'
import useIsMobile from '../../../hooks/useIsMobile';
import { IODSPriorityGroupLayout } from './types';
import GoalSelector from './GoalSelector';
import RelatedIndicators from './RelatedIndicators';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { clearTypeOfIndicator, selectedTypeIndicator, setCurrentPage, setSelectedItem } from '../../../redux/slices/odsPriorityGroups/odsPriorityGroupsSlice';
import { setSelectedPeriod } from '../../../redux/slices/periods/periodsSlice';
import DownloadCard from '../../Cards/DownloadCard';
import ResponsiveContainer from '../../../layouts/ResponsiveContainer';
import { OdsPriorityGroups, OdsPriorityGroupsWithGrouped } from '../../../interfaces/ODSPriorityGroups';
import { GROUPS, ODS } from '../../../utils/const';
import { scrollToElement } from '../../../utils/utils';
import { filterEstructuralesByPeriod } from '../../../utils/estructurals';
import ContactSection from '../../Common/ContactSection';
import Loading from '../../Loading/Loading';

type RelatedIndicatorsData =
    | (OdsPriorityGroupsWithGrouped & { type: 'grupos-prioritarios' })
    | (OdsPriorityGroups & { type: 'ods' });

const ODSPriorityGroupLayout = ({ data, titleHero, descriptionHero, imgHero }: IODSPriorityGroupLayout) => {

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();

    const currentOdsFromUrl = location.pathname.split('/').pop();
    const module = location.pathname.split('/')[2];
    const params = new URLSearchParams(location.search);

    const { loadingGroups, loadingODS, selected_type_indicator } = useAppSelector((state) => state.ods_priorityGroups)
    const selectedItem = useAppSelector(state => state.ods_priorityGroups.selectedItems[`${module}`]);
    const pagination = useAppSelector(state => state.ods_priorityGroups.selectedPagination[module]);

    const { periods } = useAppSelector((state) => state.periods);

    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [period, setPeriod] = useState<string>('');

    const prevTypeIndicator = useRef<string | null>(null);

    useEffect(() => {
        const currentId = Number(currentOdsFromUrl);
        if (!isNaN(currentId)) {
            setSelectedId(currentId);

            const itemInData = data.find(item => item.id === currentId);
            const itemInRedux = selectedItem?.id;

            if (itemInData && itemInRedux !== currentId) {
                dispatch(setSelectedItem({ module, item: itemInData }));
            }
        }
    }, [currentOdsFromUrl, data, selectedItem, module, dispatch]);

    useEffect(() => {
        const periodFromUrl = params.get('period');
        if (periodFromUrl) {
            setPeriod(periodFromUrl);
            dispatch(setSelectedPeriod({ period: periodFromUrl }));
        }
    }, [location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const typeIndicatorFromUrl = params.get('typeIndicator');

        if (typeIndicatorFromUrl) {
            if (selected_type_indicator?.name !== typeIndicatorFromUrl) {
                dispatch(selectedTypeIndicator({ typeOfIndicator: { name: typeIndicatorFromUrl } }));
            }
            if (prevTypeIndicator.current !== typeIndicatorFromUrl) {
                scrollToElement('type_indicator_groups', -50, 500);
            }
        } else {
            dispatch(clearTypeOfIndicator());
        }

        prevTypeIndicator.current = typeIndicatorFromUrl;
    }, [location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const page = params.get("page");

        if (page !== null) {
            dispatch(setCurrentPage({ module, page: Number(page) }));
        } else {
            dispatch(setCurrentPage({ module, page: 1 }));
        }
    }, [location.search, module, dispatch]);

    const handleSelect = (id: number) => {
        const typeIndicatorFromUrl = params.get('typeIndicator');
        const selectedItem = data.find(item => item.id === id);
        if (selectedItem) {
            dispatch(setSelectedItem({ module, item: selectedItem }));
            setSelectedId(id);

            const params = new URLSearchParams(location.search);
            if (period) {
                params.set('period', period);
            }

            if (typeIndicatorFromUrl) {
                params.delete('typeIndicator');
            }

            navigate(`/monitor/${module}/${id}?${params.toString()}`);
        }
    };

    const handlePeriod = (period: string) => {
        if (!period) return
        const year = period.split(" ").pop() || '';
        dispatch(setSelectedPeriod({ period: year }))
        setPeriod(year)
        params.set('period', year);
        navigate({ search: params.toString() })
    }

    const handlePageChange = (page: number) => {
        const params = new URLSearchParams(location.search);
        params.set('page', page.toString());
        navigate({ search: params.toString() });
        dispatch(setCurrentPage({ module, page: page }));
    };

    const handleSelectTypeIndicator = (typeOfIndicatorName: string) => {
        const params = new URLSearchParams(location.search);
        params.set('typeIndicator', typeOfIndicatorName);
        params.delete('page');
        navigate({ search: params.toString() });
        dispatch(selectedTypeIndicator({ typeOfIndicator: { name: typeOfIndicatorName } }));
    };

    const relatedData: RelatedIndicatorsData | null =
        selectedItem && module === ODS
            ? { ...(selectedItem as OdsPriorityGroups), type: 'ods' }
            : selectedItem && module === GROUPS
                ? { ...filterEstructuralesByPeriod(selectedItem as OdsPriorityGroupsWithGrouped, period), type: 'grupos-prioritarios' }
                : null;

    const isLoaded =
        (module === 'ods' && loadingODS === 'succeeded') ||
        (module === 'grupos-prioritarios' && loadingGroups === 'succeeded');

    return (
        <>
            <Hero
                breadcrumbs={<Breadcrumbs />}
                title={titleHero}
                paragraph={descriptionHero}
                layout="row"
                media={{
                    type: 'image',
                    src: imgHero,
                    url: '',
                    alt: 'visualizador',
                    className: 'hidden lg:inline lg:w-[304px] lg:h-[172px] xl:w-[360px] xl:h-[204px] object-contain'
                }}
                expandableDescription={isMobile ? true : false}
                buttonExpandableDescriptionClassName="md:hidden"
                containerHeightClassName='h-[28.25rem] sm:h-[27.5rem]'
                backgroundClassName={{ backgroundColor: '#E3DDD8' }}
                titleClassName='text-brand-green-20'
            />
            {isLoaded ?
                <main className="flex flex-col items-center" id='ods-priority_groups'>
                    <ResponsiveContainer>
                        <GoalSelector
                            data={data}
                            periods={periods ?? []}
                            handlePeriod={handlePeriod}
                            handleSelect={handleSelect}
                            selectedId={selectedId}
                            selectedPeriod={period}
                            isMobile={isMobile}
                            module={module}
                        />
                        <RelatedIndicators
                            data={relatedData}
                            arithmeticalEvaluation={
                                period && selectedItem?.sumatoria && selectedItem.sumatoria[period]
                                    ? [selectedItem.sumatoria[period]]
                                    : []
                            }
                            period={period}
                            pagination={pagination}
                            handlePageChange={handlePageChange}
                            handleSelectTypeIndicator={handleSelectTypeIndicator}
                            selectedTypeIndicator={selected_type_indicator?.name ?? ''}
                        />
                        {selectedItem?.pdf && selectedItem?.pdf.length > 0 && (
                            <DownloadCard
                                title='Descargá el PDF con información detallada de la nota metodológica'
                                fileName={selectedItem?.pdf}
                                module={module}
                            />
                        )}
                    </ResponsiveContainer>
                    <ContactSection />
                </main>
                : <div className='z-10 flex-1 w-full flex items-center justify-center'>
                    <Loading size={40} />
                </div>
            }

        </>
    )
}

export default ODSPriorityGroupLayout